import { useQuery } from '@tanstack/react-query'
import { getBackupCodesCount, getMfaStatus } from '../../api/mfa'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'

export type MfaBackend = {
  /** The backend's MFA record is on. Without it, regenerating recovery codes is refused (400). */
  enabled: boolean
  emailConfigured: boolean
  recoveryRemaining: number
  recoveryTotal: number
}

/**
 * What the backend knows about the account's backup methods: whether email
 * codes are on and how many recovery codes are left. A COUNT — the codes
 * themselves never enter the query cache (they exist only inside the
 * regenerate sheet).
 *
 * Firebase's factors are read elsewhere; this failing must not hide them.
 */
export function useMfaBackend(): AsyncState<MfaBackend> {
  const query = useQuery({
    queryKey: keys.mfa.status(),
    queryFn: async (): Promise<MfaBackend> => {
      const [status, count] = await Promise.all([getMfaStatus(), getBackupCodesCount()])
      return {
        enabled: status.enabled,
        emailConfigured: status.emailConfigured,
        recoveryRemaining: count.remaining,
        recoveryTotal: count.total,
      }
    },
  })
  return fromQuery(query, {
    message: () => 'We couldn’t load your backup methods. Your authenticator app and phone are unaffected.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}
